import { PaginationType } from 'typings';
import { useCallback, useState } from 'react';
import { useItemSupplierListQuery } from 'api';
import { dateFormatter } from 'constants/index';
import { ADMIN_ROUTES } from 'constants/routes';
import { FilterValue } from 'antd/es/table/interface';
import { useNavigate, useParams } from 'react-router-dom';
import { TablePaginationConfig, TableProps } from 'components';
import { ItemSupplierDataType } from 'feature/itemTabs/itemSuppliers/index';

export interface IFilterParams {
  name?: string;
  tags?: string[];
  countryId?: string;
  industryId?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
  minPurchaseRequests?: number;
  maxPurchaseRequests?: number;
}

const initialPagination: PaginationType = { page: 1, size: 10 };
export const useItemSuppliers = () => {
  const [openFilters, setOpenFilters] = useState(false);
  const [filters, setFilters] = useState<IFilterParams>({});
  const [pagination, setPagination] = useState<PaginationType>(initialPagination);
  const navigate = useNavigate();
  const { id } = useParams() as { id: string };
  const { data: { content = [], totalElements = 0 } = {}, isLoading } = useItemSupplierListQuery({
    itemId: id,
    size: pagination.size,
    page: pagination.page,
    filters,
  });
  const onOpenFilters = useCallback(() => setOpenFilters(true), []);
  const onCloseFilters = useCallback(() => setOpenFilters(false), []);

  const onRedirectToSupplier = useCallback(
    (id: string) => navigate(`${ADMIN_ROUTES.suppliers.view}/${id}/general`),
    [navigate]
  );

  const handleTableChange: TableProps<ItemSupplierDataType>['onChange'] = useCallback(
    (pagination: TablePaginationConfig, _filters: Record<string, FilterValue | null>) => {
      setPagination({
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      });
    },
    []
  );
  const applyFilters = useCallback(
    (values: Record<string, any>) => {
      const _filters: any = {
        ...(values.supplierId && { supplierId: values.supplierId }),
        ...(values.createDate?.[0] && {
          createdDateFrom: dateFormatter(values.createDate[0], 'start'),
        }),
        ...(values.createDate?.[1] && {
          createdDateTo: dateFormatter(values.createDate[1], 'end'),
        }),
        ...(values.expiryDate?.[0] && {
          expiryDateFrom: dateFormatter(values.expiryDate[0], 'start'),
        }),
        ...(values.expiryDate?.[1] && {
          expiryDateTo: dateFormatter(values.expiryDate[1], 'end'),
        }),
      };
      setFilters(_filters);
      setPagination((oState) => ({
        ...oState,
        page: 1,
      }));
      onCloseFilters();
    },
    [onCloseFilters]
  );

  const resetFilters = useCallback(() => setFilters({}), []);

  return {
    onOpenFilters,
    onCloseFilters,
    data: content?.length ? content : [],
    total: totalElements ?? 0,
    handleTableChange,
    onRedirectToSupplier,
    openFilters,
    applyFilters,
    resetFilters,
    pagination,
    isLoading,
  };
};
